import type {
  GetPaginatedItemsRequest,
  PaginatedGroup,
  PaginatedGroupedItemsDataBase,
  PaginatedMemexItemsDataBase,
  SliceData,
} from '../../../api/memex-items/contracts'
import type {MemexItemModel} from '../../../models/memex-item-model'

// Represents the data for a single page / query of memex items. This is the data
// that is returned by a call to `queryClient.getQueryData` for one of the `paginated-memex-items` queries.
export type MemexItemsPageQueryData = PaginatedMemexItemsDataBase<MemexItemModel>

type GroupWithoutItems = Omit<PaginatedGroup<MemexItemModel>, 'items'>

// Represents a page of groups. The items themselves will be stored in a different page / query, but
// we also need to store the list of groups and page info for a given page of groups.
export type MemexGroupsPageQueryData = Omit<PaginatedGroupedItemsDataBase<MemexItemModel>, 'groups'> & {
  groups: Array<GroupWithoutItems>
}

export type MemexPageQueryData = MemexItemsPageQueryData | MemexGroupsPageQueryData

/**
 * If a page was not fetched using any before or after parameters,
 * then its corresponding PageParam will be undefined.
 * We give this special case a named constant to clarify its usage.
 */
export const pageParamForInitialPage = undefined

/**
 * When a query is invalidated, its current data is moved into a
 * temporary placeholder query. This 'stale' data is displayed
 * to the user until fresh data is available.
 * We use `next_placeholder` as the PageParam for this placeholder
 * query's QueryKey:
 * ['paginatedMemexItems', { q: '', sortBy: []}, undefined, 'next_placeholder']
 */
export const pageParamForNextPlaceholder = 'next_placeholder'

/**
 * Represents how a single page of memex items is requested from the server.
 * If this value is pageParamForInitialPage, it means that it was the first page that was requested
 * either via the initial JSON island, or via an API request.
 * If this value is pageParamForNextPlaceholder, it's a page of stale data to
 * temporarily display while fresh data is refetched.
 */
export type PageParam =
  | {after: string}
  | {before: string}
  | typeof pageParamForInitialPage
  | typeof pageParamForNextPlaceholder

// Each group contains a groupId which corresponds to its key in Elasticsearch.
// Each group also contains an array of page params, which are used to request
// additional pages of data from the server.
// Typically for forward pagination, the first element of pageParams will be pageParamForInitialPage,
// and subsequent elements will be {after: string}, where the value is the
// endCursor from the previous page.
// The pageParams the sits outside of the group is used for the pagination of the groups themselves,
// whereas the pageParams within a group are used for the pagination of the items within that group.

export type PageParamsByGroupId = Record<
  /** Corresponds to its a key in Elasticsearch */
  string,
  /** Indicates how many pages of data are currently loaded for the group, as well as how to request data for each page */
  Array<PageParam>
>

/**
 * Represents the metadata used when we have grouped memex items.
 */
type GroupedActiveQueriesMetadata = {
  /** Represents the pageParams for each group, keyed by groupId. */
  groups: PageParamsByGroupId
  /** Indicates how many pages of groups are loaded, as well as how to request new groups */
  pageParams: Array<PageParam>
}

/**
 * Represents the metadata used when we have ungrouped memex items.
 */
type UngroupedActiveQueriesMetadata = {
  /** Indicates how many pages of groups are loaded, as well as how to request new groups */
  pageParams: Array<PageParam>
}

/**
 * For typical forward pagination, the first element of pageParams will be pageParamForInitialPage,
 * and subsequent elements will be {after: string}, where the value is the
 * endCursor from the previous page.
 */
export type ActiveQueriesMetadata = GroupedActiveQueriesMetadata | UngroupedActiveQueriesMetadata

// Represents the parameters used in a request for paginated data that are included in our query key.
// When the local state for one of these variables changes, we will build a new query key and re-request data from the server.
export type PaginatedMemexItemsQueryVariables = Pick<
  GetPaginatedItemsRequest,
  'q' | 'sortedBy' | 'horizontalGroupedByColumnId' | 'verticalGroupedByColumnId' | 'sliceByColumnId' | 'sliceByValue'
>

export type SliceByQueryVariables = Omit<PaginatedMemexItemsQueryVariables, 'sliceByValue'>
export type SliceByQueryData = {slices: Array<SliceData>}
